import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Camera, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h <= 20; h++) {
    for (const m of ['00', '30']) {
      if (h === 20 && m === '30') continue;
      const value = `${String(h).padStart(2, '0')}:${m}`;
      const hour12 = h % 12 === 0 ? 12 : h % 12;
      slots.push({ value, label: `${hour12}:${m} ${h < 12 ? 'AM' : 'PM'}` });
    }
  }
  return slots;
})();

export default function EmbedBooking() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();  // Get slug from URL params
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sessionType, setSessionType] = useState('');
  const [preferredDate, setPreferredDate] = useState(null);
  const [preferredTime, setPreferredTime] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Send height updates to parent when in embed mode
  useEffect(() => {
    if (isEmbed && window.parent) {
      const sendHeight = () => {
        const height = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight
        );
        window.parent.postMessage({ type: 'pb-resize', height }, '*');
      };

      // Send initial height
      sendHeight();

      // Send on resize
      const resizeObserver = new ResizeObserver(sendHeight);
      resizeObserver.observe(document.body);

      // Send after state changes
      const timer = setInterval(sendHeight, 500);

      return () => {
        resizeObserver.disconnect();
        clearInterval(timer);
      };
    }
  }, [isEmbed, success, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!preferredDate) {
      setError('Please pick a preferred date.');
      return;
    }
    setLoading(true);

    try {
      const url = slug 
        ? `${API}/public/book?slug=${slug}`
        : `${API}/public/book`;
      
      await axios.post(url, {
        name,
        email,
        session_type: sessionType || undefined,
        preferred_date: format(preferredDate, 'yyyy-MM-dd'),
        preferred_time: preferredTime || undefined,
        message: message || undefined,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sessionOptions = [
    'Fitness Portrait',
    'Gym Session',
    'Outdoor & Action',
    'Competition Prep',
    'Brand & Sponsor Content',
    'Other'
  ];

  // Embed mode styles
  const embedContainerClass = isEmbed
    ? 'min-h-[600px] p-6 bg-transparent'
    : 'min-h-screen bg-background flex flex-col items-center justify-center p-4';

  const embedFormClass = isEmbed
    ? 'w-full max-w-2xl mx-auto'
    : 'w-full max-w-md';

  if (success) {
    return (
      <div className={embedContainerClass}>
        <div className={`${embedFormClass} text-center space-y-4`}>
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-full">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-green-500">Request Received!</h2>
          <p className={isEmbed ? 'text-white/70' : 'text-text-secondary'}>
            I'll be in touch within 24 hours.
          </p>
          {!isEmbed && (
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-primary hover:text-primary/80 underline"
            >
              Submit another booking
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={embedContainerClass}>
      <div className={embedFormClass}>
        {!isEmbed && (
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-2xl mb-4">
              <Camera className="w-8 h-8 text-background" />
            </div>
            <h1 className="text-3xl font-serif font-bold text-foreground mb-2">
              Book a Session
            </h1>
            <p className="text-text-secondary">
              Fill out the form below and I'll get back to you shortly
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={`w-full rounded-lg px-4 py-3 outline-none transition-all ${
                isEmbed
                  ? 'bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D2A06A] focus:bg-white/10'
                  : 'bg-surface-raised border border-border text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary'
              }`}
              placeholder="Your name"
              disabled={loading}
            />
          </div>

          {/* Email */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Email *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full rounded-lg px-4 py-3 outline-none transition-all ${
                isEmbed
                  ? 'bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D2A06A] focus:bg-white/10'
                  : 'bg-surface-raised border border-border text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary'
              }`}
              placeholder="you@example.com"
              disabled={loading}
            />
          </div>

          {/* Session Type */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Session Type
            </label>
            <select
              value={sessionType}
              onChange={(e) => setSessionType(e.target.value)}
              className={`w-full rounded-lg px-4 py-3 outline-none transition-all ${
                isEmbed
                  ? 'bg-white/5 border border-white/10 text-white focus:border-[#D2A06A] focus:bg-white/10'
                  : 'bg-surface-raised border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary'
              }`}
              disabled={loading}
            >
              <option value="">Select a session type...</option>
              {sessionOptions.map((option) => (
                <option key={option} value={option} className="bg-[#1a1a1a] text-white">
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Date */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Preferred Date *
            </label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={loading}
                  data-testid="booking-date-picker"
                  className={`w-full rounded-lg px-4 py-3 outline-none transition-all text-left flex items-center gap-2 ${
                    isEmbed
                      ? 'bg-white/5 border border-white/10 text-white focus:border-[#D2A06A] focus:bg-white/10'
                      : 'bg-surface-raised border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary'
                  }`}
                >
                  <CalendarIcon className="w-4 h-4 opacity-60" />
                  {preferredDate ? (
                    format(preferredDate, 'EEEE, MMMM d, yyyy')
                  ) : (
                    <span className={isEmbed ? 'text-white/30' : 'text-text-muted'}>Pick a date</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={preferredDate}
                  onSelect={(d) => { setPreferredDate(d); setDatePickerOpen(false); }}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Preferred Time */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Preferred Time
            </label>
            <select
              value={preferredTime}
              onChange={(e) => setPreferredTime(e.target.value)}
              data-testid="booking-time-select"
              className={`w-full rounded-lg px-4 py-3 outline-none transition-all ${
                isEmbed
                  ? 'bg-white/5 border border-white/10 text-white focus:border-[#D2A06A] focus:bg-white/10'
                  : 'bg-surface-raised border border-border text-foreground focus:border-primary focus:ring-1 focus:ring-primary'
              }`}
              disabled={loading}
            >
              <option value="">Any time / flexible</option>
              {TIME_SLOTS.map((slot) => (
                <option key={slot.value} value={slot.value} className="bg-[#1a1a1a] text-white">
                  {slot.label}
                </option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isEmbed ? 'text-white/90' : 'text-foreground'}`}>
              Message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className={`w-full rounded-lg px-4 py-3 outline-none transition-all resize-none ${
                isEmbed
                  ? 'bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-[#D2A06A] focus:bg-white/10'
                  : 'bg-surface-raised border border-border text-foreground placeholder-text-muted focus:border-primary focus:ring-1 focus:ring-primary'
              }`}
              placeholder="Tell me about your vision..."
              disabled={loading}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className={`rounded-lg p-3 text-sm ${isEmbed ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'bg-alert/10 border border-alert/20 text-alert'}`}>
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full font-semibold rounded-lg px-6 py-3 transition-all ${
              isEmbed
                ? 'bg-[#D2A06A] hover:bg-[#C29058] text-black'
                : 'bg-primary hover:bg-primary/90 text-background'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? 'Submitting...' : 'Submit Booking Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
